import dayjs from '@/config/dayjs';
import { formatNumberToCurrency } from '@/utils/currency';
import { formatAppDate } from '@/utils/date';
import { APP_DATE_FORMAT } from '@/constants/dateFormats';
import { serverGroupLabel } from '@/constants/clientWealthGrouping';
import type { AccountGridRow } from './accountsGridTypes';

export type AccountsExportFormat = 'csv' | 'xlsx';

export type AccountsExportScalar = string | number | boolean | null;

export interface AccountsExportColumn {
  field: string;
  headerName: string;
}

export interface AccountsExportTable {
  columns: AccountsExportColumn[];
  rows: AccountsExportScalar[][];
}

export type AccountsSearchField = 'Name' | 'AccountNumber';

export interface AccountsSearchExportConfig {
  searchableFields: readonly AccountsSearchField[];
  filenamePrefix: 'Accounts';
  uiDate: string;
}

export interface AccountsExportToolbarConfig {
  buildTable: () => AccountsExportTable;
  filenamePrefix: 'Accounts';
  uiDate: string;
}

export type AccountsExportBusinessField =
  | 'CustomGroup'
  | 'ServerGroup'
  | 'InstitutionName'
  | 'DataSource'
  | 'HeldBy'
  | 'Name'
  | 'AccountNumber'
  | 'Balance'
  | 'formattedSubtype'
  | 'LastUpdated'
  | 'ClassificationName';

export interface AccountExportDescriptor {
  field: AccountsExportBusinessField;
  getValue: (row: AccountGridRow) => AccountsExportScalar;
}

export interface AccountsExportColumnMetadata {
  field: string;
  headerName: string;
  visible: boolean;
  disableExport: boolean;
  groupable: boolean;
  isAutogenerated: boolean;
}

export interface AccountsExportLeafRowCandidate {
  type: 'leaf';
  row: AccountGridRow;
}

export interface AccountsExportNonLeafRowCandidate {
  type: 'group' | 'footer' | 'pinnedRow' | 'skeletonRow';
}

export type AccountsExportRowCandidate =
  | AccountsExportLeafRowCandidate
  | AccountsExportNonLeafRowCandidate;

export interface BuildAccountsExportTableOptions {
  rowCandidates: readonly AccountsExportRowCandidate[];
  columnMetadata: readonly AccountsExportColumnMetadata[];
  groupingModel: readonly string[];
  groupNotSet: string;
}

export const ACCOUNT_EXPORT_DESCRIPTORS: readonly AccountExportDescriptor[] = [
  {
    field: 'CustomGroup',
    getValue: (row) => row.CustomGroup,
  },
  {
    field: 'ServerGroup',
    // ServerGroup carries a sort-stable `id\x1flabel` key (see serverGroupKey);
    // decode it to the label the grid itself displays via valueFormatter.
    getValue: (row) => serverGroupLabel(row.ServerGroup),
  },
  {
    field: 'InstitutionName',
    getValue: (row) => row.InstitutionName,
  },
  {
    field: 'DataSource',
    getValue: (row) => row.DataSource,
  },
  {
    field: 'HeldBy',
    getValue: (row) => row.HeldBy,
  },
  {
    field: 'Name',
    getValue: (row) => row.Name,
  },
  {
    field: 'AccountNumber',
    getValue: (row) => row.AccountNumber ?? '',
  },
  {
    field: 'Balance',
    getValue: (row) => formatNumberToCurrency(row.Balance),
  },
  {
    field: 'formattedSubtype',
    getValue: (row) => row.formattedSubtype,
  },
  {
    field: 'LastUpdated',
    getValue: (row) =>
      formatAppDate(row.LastUpdated, { format: 'M/D/YYYY', fallback: '' }),
  },
  {
    field: 'ClassificationName',
    getValue: (row) => row.ClassificationName,
  },
];

interface ResolvedExportColumn {
  column: AccountsExportColumn;
  descriptor: AccountExportDescriptor;
  isGrouping: boolean;
}

type AccountsExportSourceScalar = AccountsExportScalar | undefined;

const technicalColumnFields: readonly string[] = ['actions', 'listColumn'];

export function buildAccountsExportFilename(
  uiDate: string,
  format: AccountsExportFormat,
): string {
  const parsed = dayjs(uiDate, APP_DATE_FORMAT, true);
  if (!parsed.isValid()) {
    throw new Error(`Invalid Accounts export UI date: "${uiDate}"`);
  }

  return `Accounts ${parsed.format('MM-DD-YYYY')}.${format}`;
}

function isTechnicalColumn(metadata: AccountsExportColumnMetadata): boolean {
  return (
    metadata.isAutogenerated ||
    metadata.field.startsWith('__') ||
    technicalColumnFields.includes(metadata.field)
  );
}

function descriptorFor(field: string): AccountExportDescriptor {
  const descriptor = ACCOUNT_EXPORT_DESCRIPTORS.find(
    (candidate) => candidate.field === field,
  );

  if (!descriptor) {
    throw new Error(`Unsupported Accounts export field: "${field}"`);
  }

  return descriptor;
}

function uniqueGroupingFields(groupingModel: readonly string[]): string[] {
  const fields: string[] = [];
  const seen = new Set<string>();

  for (const field of groupingModel) {
    if (!seen.has(field)) {
      seen.add(field);
      fields.push(field);
    }
  }

  return fields;
}

function validateBusinessMetadata(
  columnMetadata: readonly AccountsExportColumnMetadata[],
  groupingFields: readonly string[],
): void {
  for (const metadata of columnMetadata) {
    if (isTechnicalColumn(metadata)) {
      continue;
    }

    const participatesInContract =
      metadata.groupable ||
      (metadata.visible && !metadata.disableExport) ||
      groupingFields.includes(metadata.field);

    if (participatesInContract) {
      descriptorFor(metadata.field);
    }
  }
}

function metadataForGroupingField(
  field: string,
  columnMetadata: readonly AccountsExportColumnMetadata[],
): AccountsExportColumnMetadata {
  const metadata = columnMetadata.find(
    (candidate) => candidate.field === field && !isTechnicalColumn(candidate),
  );

  if (!metadata) {
    throw new Error(
      `Missing Accounts export column metadata for grouping field: "${field}"`,
    );
  }

  return metadata;
}

function exportColumnFromMetadata(
  metadata: AccountsExportColumnMetadata,
): AccountsExportColumn {
  if (metadata.headerName.trim().length === 0) {
    throw new Error(
      `Missing Accounts export header for business field: "${metadata.field}"`,
    );
  }

  return {
    field: metadata.field,
    headerName: metadata.headerName,
  };
}

function resolveExportColumns(
  columnMetadata: readonly AccountsExportColumnMetadata[],
  groupingModel: readonly string[],
): ResolvedExportColumn[] {
  const groupingFields = uniqueGroupingFields(groupingModel);
  validateBusinessMetadata(columnMetadata, groupingFields);

  const resolved: ResolvedExportColumn[] = [];
  const includedFields = new Set<string>();

  for (const field of groupingFields) {
    const descriptor = descriptorFor(field);
    const metadata = metadataForGroupingField(field, columnMetadata);

    resolved.push({
      column: exportColumnFromMetadata(metadata),
      descriptor,
      isGrouping: true,
    });
    includedFields.add(field);
  }

  for (const metadata of columnMetadata) {
    if (
      isTechnicalColumn(metadata) ||
      !metadata.visible ||
      metadata.disableExport ||
      includedFields.has(metadata.field)
    ) {
      continue;
    }

    resolved.push({
      column: exportColumnFromMetadata(metadata),
      descriptor: descriptorFor(metadata.field),
      isGrouping: false,
    });
    includedFields.add(metadata.field);
  }

  return resolved;
}

function groupingValue(
  value: AccountsExportSourceScalar,
  groupNotSet: string,
): AccountsExportScalar {
  if (value === null || value === undefined) {
    return groupNotSet;
  }

  if (typeof value === 'string' && value.trim().length === 0) {
    return groupNotSet;
  }

  return value;
}

function exportValue(value: AccountsExportSourceScalar): AccountsExportScalar {
  return value === undefined ? '' : value;
}

export function buildAccountsExportTable({
  rowCandidates,
  columnMetadata,
  groupingModel,
  groupNotSet,
}: BuildAccountsExportTableOptions): AccountsExportTable {
  const resolvedColumns = resolveExportColumns(columnMetadata, groupingModel);
  const rows: AccountsExportScalar[][] = [];

  for (const candidate of rowCandidates) {
    if (candidate.type !== 'leaf') {
      continue;
    }

    rows.push(
      resolvedColumns.map((resolvedColumn) => {
        const value = resolvedColumn.descriptor.getValue(candidate.row);
        return resolvedColumn.isGrouping
          ? groupingValue(value, groupNotSet)
          : exportValue(value);
      }),
    );
  }

  return {
    columns: resolvedColumns.map((resolvedColumn) => resolvedColumn.column),
    rows,
  };
}
